/**
 * SOFSEK — Org Hierarchy Console (§6). Monitored-enterprise org tree, distinct
 * from CSM's own organization structure. Phase 1 delivers maintenance (create /
 * edit / indented list sorted by hierarchy_path); click-to-drill-down filtering
 * of dashboards/grids by branch lands with Phase 3+ once there is data to filter.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { hasAccess, type AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const PAGE_CODE = 'SFK.ORG_NODE.LIST';
const inputCls = 'mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm';
const labelCls = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';

interface CodeOpt { code: string; name: string; }
interface NodeRow {
  org_node_id: number;
  parent_org_node_id: number | null;
  org_node_type_code: string;
  internal_code: string;
  name: string;
  hierarchy_path: string | null;
  is_active: 'Y' | 'N';
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'name', header: 'Name', defaultWidth: 260 },
  { key: 'internal_code', header: 'Code', defaultWidth: 140 },
  { key: 'org_node_type', header: 'Type', defaultWidth: 140 },
  { key: 'parent', header: 'Parent', defaultWidth: 200 },
  { key: 'is_active', header: 'Active', defaultWidth: 90 },
];

function depthOf(path: string | null): number {
  if (!path) return 0;
  return Math.max(0, (path.match(/\//g)?.length ?? 1) - 1);
}

function OrgNodeInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<NodeRow[]>([]);
  const [types, setTypes] = useState<CodeOpt[]>([]);
  const [nodeUsers, setNodeUsers] = useState<{ app_user_id: number; full_name: string; home_org_node_id: number | null }[]>([]);
  const [filter, setFilter] = useState('');
  const [activeFacet, setActiveFacet] = useState<string[]>(['Y']);
  const [typeFacet, setTypeFacet] = useState<string[]>([]);
  const [editing, setEditing] = useState<NodeRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const detail = useExpandedRows<number>();
  // §6/§17: csm_role_page_access decides which write affordances this user is
  // offered. The server refuses the write regardless (RLS, via the Common
  // Routine) — this stops the page offering an action that can only error.
  const canAdd    = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [nodeRes, typeRes, userRes] = await Promise.all([
        apiDb.from('sfk_org_node').select('org_node_id, parent_org_node_id, org_node_type_code, internal_code, name, hierarchy_path, is_active').order('hierarchy_path'),
        apiDb.from('sfk_org_node_type').select('org_node_type_code, sfk_org_node_type_tl ( locale_code, display_text )'),
        apiDb.from('sfk_subject_profile').select('user_id, home_org_node_id, csm_user_master!fk_sfk_subject_profile_user ( display_name )'),
      ]);
      if (nodeRes.error) throw nodeRes.error;
      if (typeRes.error) throw typeRes.error;
      if (userRes.error) throw userRes.error;
      setRows((nodeRes.data ?? []) as unknown as NodeRow[]);
      setNodeUsers(((userRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
        app_user_id: r.user_id as number,
        full_name: ((r.csm_user_master ?? {}) as { display_name?: string }).display_name ?? String(r.user_id),
        home_org_node_id: (r.home_org_node_id as number | null) ?? null,
      })));
      setTypes(((typeRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
        code: r.org_node_type_code as string,
        name: ((r.sfk_org_node_type_tl ?? []) as { locale_code: string; display_text: string }[]).find(x => x.locale_code === 'EN_US')?.display_text ?? (r.org_node_type_code as string),
      })));
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

   
  useEffect(() => { void reload(); }, []);

  const resolveNameForType = (code: string) => types.find(x => x.code === code)?.name ?? code;
  const nodeName = (id: number | null) => (id == null ? t('sfk.org_node.root', '(root)') : rows.find(r => r.org_node_id === id)?.name ?? '');

  // §17.B13 — related children: sub-branches + app users homed at this node.
  const childNodesOf = (id: number) => rows.filter(r => r.parent_org_node_id === id);
  const usersAt = (id: number) => nodeUsers.filter(u => u.home_org_node_id === id);
  const hasRelated = (id: number) => childNodesOf(id).length > 0 || usersAt(id).length > 0;
  const buildRelatedGroups = (id: number): RelatedChildGroup[] => [
    { key: 'subnodes', title: t('sfk.org_node.subbranches', 'Sub-branches'),
      items: childNodesOf(id).map(n => ({ id: n.org_node_id, label: n.name, title: n.internal_code })) },
    { key: 'users', title: t('sfk.org_node.app_users', 'App Users'),
      items: usersAt(id).map(u => ({ id: u.app_user_id, label: u.full_name })) },
  ];

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(r =>
      (!q || r.name.toLowerCase().includes(q) || r.internal_code.toLowerCase().includes(q)) &&
      (activeFacet.length === 0 || activeFacet.includes(r.is_active)) &&
      (typeFacet.length === 0 || typeFacet.includes(r.org_node_type_code)));
  }, [rows, filter, activeFacet, typeFacet]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('sfk.org_node.title', 'Org Hierarchy Console')}
          description={t('sfk.org_node.description', 'The monitored-enterprise org tree — branches and the app users homed at each.')}
          actions={<>
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
            {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> {t('common.new', 'New')}</button>)}
          </>}
        />
        <StatStrip stats={[
          { value: rows.length, label: t('sfk.org_node.stat.total', 'Nodes') },
          { value: rows.filter(r => r.is_active === 'Y').length, label: t('common.boolean.active', 'Active') },
          { value: nodeUsers.length, label: t('sfk.org_node.stat.users', 'App users') },
        ]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('sfk.org_node.facet.type', 'Type')} options={types.map(x => ({ value: x.code, label: x.name }))} selected={typeFacet} onChange={setTypeFacet} />
            <FacetFilter label={t('common.boolean.active', 'Active')} options={[{ value: 'Y', label: t('common.boolean.yes', 'Yes') }, { value: 'N', label: t('common.boolean.no', 'No') }]} selected={activeFacet} onChange={setActiveFacet} />
            <ClearFiltersButton active={filter.trim() !== '' || activeFacet.length > 0 || typeFacet.length > 0} onClear={() => { setFilter(''); setActiveFacet([]); setTypeFacet([]); }} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="border-b border-csm-border px-1 py-1.5 text-left" style={{ width: '1%' }} aria-hidden>&nbsp;</th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.org_node_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.org_node_id)} hasChildren={hasRelated(r.org_node_id)} onToggle={() => detail.toggle(r.org_node_id)}
                        expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'name' && <span style={{ paddingLeft: `${depthOf(r.hierarchy_path) * 16}px` }}>{r.name}</span>}
                        {col.key === 'internal_code' && <span className="font-mono text-csm-sm">{r.internal_code}</span>} {/* fk-display-ok: node's own reference code */}
                        {col.key === 'org_node_type' && resolveNameForType(r.org_node_type_code)}
                        {col.key === 'parent' && nodeName(r.parent_org_node_id)}
                        {col.key === 'is_active' && <YesNoBadge value={r.is_active} />}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                      {canChange && (<button onClick={() => setEditing(r)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('common.edit', 'Edit')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.org_node_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.org_node_id)} emptyLabel={t('common.related.none', 'No related records.')} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {(editing || creating) && (
        <NodeModal session={session} row={editing} nodes={rows} types={types}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}
    </div>
  );
}

function NodeModal({ session, row, nodes, types, onClose, onSaved }: {
  session: AuthSession; row: NodeRow | null; nodes: NodeRow[]; types: CodeOpt[]; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isCreate = row === null;
  const [code, setCode] = useState(row?.internal_code ?? '');
  const [name, setName] = useState(row?.name ?? '');
  const [typeCode, setTypeCode] = useState(row?.org_node_type_code ?? types[0]?.code ?? '');
  const [parentId, setParentId] = useState(row?.parent_org_node_id != null ? String(row.parent_org_node_id) : '');
  const [isActive, setIsActive] = useState(!row || row.is_active === 'Y');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!code.trim()) throw new Error(t('common.err.code_required', 'Code is required.'));
      if (!name.trim()) throw new Error(t('sfk.org_node.err.name_required', 'Name is required.'));

      const payload = {
        name: name.trim(),
        org_node_type_code: typeCode,
        parent_org_node_id: parentId ? Number(parentId) : null,
        is_active: isActive ? 'Y' : 'N',
      };
      if (row === null) {
        const { error: e } = await routedInsert(session, 'sfk_org_node', { internal_code: code.trim().toUpperCase(), ...payload });
        if (e) throw new Error(e.message);
      } else {
        const { error: e } = await routedUpdate(session, 'sfk_org_node', 'org_node_id', row.org_node_id, payload);
        if (e) throw new Error(e.message);
      }
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal
      title={isCreate ? t('sfk.org_node.new', 'New Org Node') : `${t('common.edit', 'Edit')} — ${row?.name ?? ''}`}
      onClose={onClose}
      width="md"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </>
      }
    >
      <PersonalizedField componentKey="modal" fieldKey="internal_code" label={t('common.code', 'Code')}>
        <label className={labelCls}>{t('common.code', 'Code')}</label>
        <input value={code} disabled={!isCreate} onChange={e => setCode(e.target.value.toUpperCase())} className={`${inputCls} font-mono`} />
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="name" label={t('common.name', 'Name')}>
        <label className={labelCls}>{t('common.name', 'Name')}</label>
        <input value={name} onChange={e => setName(e.target.value)} className={inputCls} />
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="org_node_type_code" label={t('sfk.org_node.type', 'Type')}>
        <label className={labelCls}>{t('sfk.org_node.type', 'Type')}</label>
        <select value={typeCode} onChange={e => setTypeCode(e.target.value)} className={`${inputCls} bg-white`}>
          {types.map(x => <option key={x.code} value={x.code}>{x.name}</option>)}
        </select>
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="parent_org_node_id" label={t('sfk.org_node.parent', 'Parent')}>
        <label className={labelCls}>{t('sfk.org_node.parent', 'Parent')}</label>
        <select value={parentId} onChange={e => setParentId(e.target.value)} className={`${inputCls} bg-white`}>
          <option value="">{t('sfk.org_node.root', '(root)')}</option>
          {nodes.filter(n => n.is_active === 'Y' && n.org_node_id !== row?.org_node_id).map(n => <option key={n.org_node_id} value={n.org_node_id}>{n.name}</option>)}
        </select>
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="is_active" label={t('common.boolean.active', 'Active')}>
        <label className="mb-3 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="h-4 w-4 accent-gray-700" />
          {t('common.boolean.active', 'Active')}
        </label>
      </PersonalizedField>

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

export function SfkOrgNodePage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <OrgNodeInner session={session} />
    </PersonalizationProvider>
  );
}
